import { useMemo, useState } from 'react';

import parse, { DOMNode, Element } from 'html-react-parser';
import { marked } from 'marked';
import { useParams } from 'react-router-dom';

import { formatContent, REF_PLACEHOLDER_TAG } from './kim-message';
import { Reference } from './reference';
import { KIM_MARKED_OPTIONS } from '../../utils/kim-markdown';
import {
  InconsistencyBlock as InconsistencyBlockType,
  InconsistencyReference,
  ReferenceType,
} from '@/../../backend/src/types/types';
import {
  type KimChronologyFactDetail,
  useGetFactDetailsForKimChronology,
} from '@/api/queries/useGetFactDetailsForKimChronology';
import { Icon } from '@/new/components/icon';
import { DocComparerGroup, type ComparerFactEntry } from '@/new/components/inconsistency/doc-comparer-group';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent } from '@/new/components/shadcn/dialog';

interface KimInconsistencyBlockProps {
  block: InconsistencyBlockType;
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
}

const CITABLE_REF_TYPES = new Set<ReferenceType>([
  ReferenceType.Fact,
  ReferenceType.Chunk,
  ReferenceType.Document,
  ReferenceType.ThreadFileChunk,
]);

export function KimInconsistencyBlock({ block, referenceIndexMap, onReferenceClick }: KimInconsistencyBlockProps) {
  const references = block.references ?? [];
  const description = renderDescription(block.description ?? [], referenceIndexMap, onReferenceClick);
  const [compareOpen, setCompareOpen] = useState(false);

  return (
    <div className="mx-auto my-4 max-w-3xl rounded-xl border border-red-400/80 bg-white p-3.5 px-3 font-sans">
      <div className="mb-1.75 flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1.5 pl-2 font-sans text-[11px] font-medium tracking-wide text-red-600 uppercase">
          <Icon className="lucide--shield-alert" />
          <span className="mt-px">Inconsistency</span>
        </div>
        {references.length > 0 && (
          <Button onClick={() => setCompareOpen(true)} size="sm" className="-mt-px py-0.75">
            <Icon className="lucide--flip-horizontal-2 text-[11px]" />
            Compare evidence
          </Button>
        )}
      </div>

      <div className="prose prose-sm prose-p:my-1.5 prose-p:leading-normal prose-p:text-gray-800 max-w-none px-2.5">
        {description}
      </div>

      <CompareEvidenceDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        references={references}
        onReferenceClick={(id, refType) => {
          setCompareOpen(false);
          onReferenceClick?.(id, refType);
        }}
      />
    </div>
  );
}

function renderDescription(
  blocks: InconsistencyBlockType['description'],
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>,
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void,
) {
  // formatContent already handles text + reference inline; artifact/survey/inconsistency
  // nested inside a description aren't expected, so they collapse to empty strings.
  const combined = formatContent(blocks);
  const rawHtml = marked.parse(combined, KIM_MARKED_OPTIONS) as string;
  const html = rawHtml.replace(new RegExp(`(<\\/${REF_PLACEHOLDER_TAG}>)\\s+(<${REF_PLACEHOLDER_TAG})`, 'g'), '$1$2');

  return parse(html, {
    replace: (domNode: DOMNode) => {
      if (domNode instanceof Element && domNode.name === REF_PLACEHOLDER_TAG) {
        const id = domNode.attribs['data-id'];
        const refType = domNode.attribs['data-ref-type'] as ReferenceType;
        return (
          <Reference
            index={referenceIndexMap.get(id)?.index ?? 0}
            id={id}
            referenceType={refType}
            onClick={CITABLE_REF_TYPES.has(refType) ? onReferenceClick : undefined}
          />
        );
      }
    },
  });
}

// Stable order: keep the first-seen name first so streamed refs don't reflow buckets.
function groupReferencesByName(refs: InconsistencyReference[]): { name: string; refs: InconsistencyReference[] }[] {
  const groups: { name: string; refs: InconsistencyReference[] }[] = [];
  for (const ref of refs) {
    const existing = groups.find((g) => g.name === ref.name);
    if (existing) existing.refs.push(ref);
    else groups.push({ name: ref.name, refs: [ref] });
  }
  return groups;
}

function CompareEvidenceDialog({
  open,
  onOpenChange,
  references,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  references: InconsistencyReference[];
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
}) {
  const { matterId, subsetId } = useParams<{ matterId: string; subsetId: string }>();

  const groups = useMemo(() => groupReferencesByName(references), [references]);

  const factIds = useMemo(
    () => references.filter((r) => r.reference.refType === ReferenceType.Fact).map((r) => r.reference.id),
    [references],
  );

  const { data: factSummaries } = useGetFactDetailsForKimChronology(matterId, subsetId, factIds);

  const comparerFactsByGroup = useMemo(() => {
    const summaryMap = new Map<string, KimChronologyFactDetail>();
    factSummaries?.forEach((s) => summaryMap.set(s.id, s));

    return groups.map(({ name, refs }) => {
      const facts: ComparerFactEntry[] = refs
        .filter((r) => r.reference.refType === ReferenceType.Fact)
        .map((r) => summaryMap.get(r.reference.id))
        .filter((s): s is KimChronologyFactDetail => Boolean(s))
        .map((s) => ({
          fact: {
            id: s.id,
            description: s.description,
            locations: s.sources.map((source) => ({
              doc: {
                id: source.docId,
                file: { name: source.fileName },
                type: source.docType,
              },
            })),
          },
        }));
      return { name, facts };
    });
  }, [groups, factSummaries]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-9/10 max-h-none w-9/10 max-w-none gap-4 pb-3">
        {comparerFactsByGroup.map(({ name, facts }) =>
          facts.length > 0 ? <DocComparerGroup key={name} title={name} facts={facts} /> : null,
        )}
      </DialogContent>
    </Dialog>
  );
}
